import type { AxiosInstance, Method } from 'lib/axios';
import { APIConfig } from 'reactApp/api/APIConfig';
import { IS_MY_TEAM, IS_NEED_SILENT_AUTH } from 'reactApp/appHelpers/configHelpers';
import { UserNoAuthError } from 'reactApp/errors/serverErrors/UserNoAuthError';

import { type Args, AbstractAPICall } from './AbstractAPICall';
import { getAxiosInstance } from './axiosFactory';
import { SDCAPICall } from './methods/SDCAPICall';
import { TokensCSRFAPICall } from './methods/TokensCSRFAPICall';

function error403(error) {
    if (error instanceof UserNoAuthError && !error.isNotAuthorizedError()) {
        const api = error.isCSRFTokenError() ? new TokensCSRFAPICall() : new SDCAPICall({ error });
        return api.makeRequest();
    }

    if (error instanceof UserNoAuthError && error.isNotAuthorizedError() && IS_MY_TEAM && IS_NEED_SILENT_AUTH) {
        return import('reactApp/modules/vkTeam/vkTeam.module').then((module) => module.VKTeamsClient.authWithSilentToken());
    }

    throw error;
}

class APICall<TRes = any, TArgs = Args> extends AbstractAPICall<TRes, TArgs> {
    private _axios: AxiosInstance | null = null;

    protected _defaultPayload = {
        'x-email': APIConfig.get('user') || 'anonym',
    };

    _errorHandlers = [error403];

    _abstractGetAxiosInstance(): AxiosInstance | null {
        if (this._axios === null) {
            this._axios = getAxiosInstance(this.version);
        }

        return this._axios;
    }
}

class APICallV0<TRes = any, TArgs = Args> extends APICall<TRes, TArgs> {}

class APICallV1<TRes = any, TArgs = Args> extends APICall<TRes, TArgs> {
    version = 1;
}

class APICallV2<TRes = any, TArgs = Args> extends APICall<TRes, TArgs> {
    version = 2;
}

class APICallV2Get<TRes = any, TArgs = Args> extends APICallV2<TRes, TArgs> {
    _type: Method = 'get';
    defaultParams = this._defaultPayload;
}

class APICallV2Post<TRes = any, TArgs = Args> extends APICallV2<TRes, TArgs> {
    _type: Method = 'post';
    defaultData = this._defaultPayload;
}

class APICallV3<TRes = any, TArgs = Args> extends APICall<TRes, TArgs> {
    version = 3;
}

class APICallV3Get<TRes = any, TArgs = Args> extends APICallV3<TRes, TArgs> {
    _type: Method = 'get';
    defaultParams = this._defaultPayload;
}

class APICallV3Post<TRes = any, TArgs = Args> extends APICallV3<TRes, TArgs> {
    _type: Method = 'post';
    defaultData = this._defaultPayload;
}

class APICallV3Put<TRes = any, TArgs = Args> extends APICallV3<TRes, TArgs> {
    _type: Method = 'put';
}

class APICallV4<TRes = any, TArgs = Args> extends APICall<TRes, TArgs> {
    version = 4;
}

class APICallV4Post<TRes = any, TArgs = Args> extends APICallV4<TRes, TArgs> {
    _type: Method = 'post';
    defaultData = this._defaultPayload;
}

export { APICallV0, APICallV1, APICallV2Get, APICallV2Post, APICallV3Get, APICallV3Post, APICallV3Put, APICallV4, APICallV4Post };
